/**
 * CARREGAMENTO DE CONTEXTO — LISTA VAZIA NÃO É ERRO DE PERMISSÃO.
 *
 * O padrão espalhado pelas páginas era:
 *
 *     const items = await service.listX(ctx).catch(() => []);
 *
 * Com isso, um `403 Permission denied` e um "não há registros" produziam
 * exatamente o mesmo resultado — e a tela anunciava
 * "Nenhuma OS cadastrada para este contexto" quando a OS existia e a
 * consulta havia sido NEGADA. O diagnóstico saía errado: procurava-se um
 * cadastro que já estava lá.
 *
 * `loadList` separa os três casos, que são coisas diferentes:
 *
 *     ok        a consulta respondeu — `items` pode estar legitimamente vazio
 *     denied    faltou permissão de leitura
 *     failed    erro técnico
 *
 * Quem chama decide o que desenhar, mas não pode mais confundir os três.
 */

import { AppError, ErrorCode } from "../../shared/errors/app-error.js";

export interface ContextList<T> {
  readonly items: readonly T[];
  readonly status: "ok" | "denied" | "failed";
  /** Mensagem pronta para a tela. Vazia quando `ok`. */
  readonly message: string;
  /** O que dizer no lugar de "nenhum registro" quando não foi possível ler. */
  readonly placeholder: string;
}

const ok = <T>(items: readonly T[]): ContextList<T> => ({ items, status: "ok", message: "", placeholder: "" });

/**
 * Executa a consulta e classifica a resposta.
 *
 * `label` é o nome do que se tentou carregar ("Ordens de Serviço"), usado
 * para a mensagem dizer QUAL leitura falhou — "não foi possível carregar"
 * sem dizer o quê obriga quem lê a adivinhar.
 */
export async function loadList<T>(label: string, run: () => Promise<readonly T[] | null | undefined>): Promise<ContextList<T>> {
  try {
    const items = await run();
    return ok(Array.isArray(items) ? items : []);
  } catch (error) {
    const denied = error instanceof AppError && error.code === ErrorCode.Authorization;
    if (denied) {
      const message = `Você não possui permissão de leitura para ${label}. Peça à Administração a permissão correspondente — o cadastro pode existir e não estar sendo mostrado.`;
      return { items: [], status: "denied", message, placeholder: `Sem permissão para ler ${label}` };
    }
    const technical = error instanceof AppError ? (error.safeMessage || error.message) : String((error as { message?: string })?.message ?? error);
    // O erro técnico vai para o console porque a tela não é lugar de pilha,
    // mas NÃO é silenciado: a mensagem diz que houve falha, não "não há".
    console.error(`[CONTEXTO] Falha ao carregar ${label}:`, error);
    return {
      items: [],
      status: "failed",
      message: `Não foi possível carregar ${label}: ${technical}`,
      placeholder: `Falha ao carregar ${label}`
    };
  }
}

/**
 * O texto do `<option>` vazio de um select da cascata.
 *
 * Só diz "Nenhum … cadastrado" quando a consulta REALMENTE respondeu vazia.
 * Se foi negada ou falhou, diz isso — é a diferença entre "não existe" e
 * "não consegui ver".
 */
export function emptyOptionLabel<T>(list: ContextList<T>, whenEmpty: string, whenReady: string): string {
  if (list.status !== "ok") return list.placeholder;
  return list.items.length === 0 ? whenEmpty : whenReady;
}

/** As mensagens de todas as listas que não puderam ser lidas, sem repetição. */
export function contextLoadIssues(...lists: ReadonlyArray<ContextList<unknown>>): string[] {
  return [...new Set(lists.filter(l => l.status !== "ok").map(l => l.message))];
}

/**
 * O MESMO TRATAMENTO, EM TODAS AS TELAS, COM UM CAMPO SÓ.
 *
 * Cada página da cascata carrega sete ou oito listas. Guardar o resultado de
 * cada uma em um campo próprio multiplicaria por oito o estado de cada tela e
 * convidaria a divergência — foi assim que `.catch(() => [])` se espalhou por
 * 137 lugares, cada um "resolvendo" o problema à sua maneira.
 *
 * O rastreador guarda os resultados por rótulo. A página ganha UM campo e
 * troca `await svc.listX(...).catch(() => [])` por
 * `await this.ctxLoad.load("Projetos", () => svc.listX(...))`.
 *
 * A política de tratamento continua sendo uma só: esta.
 */
export class ContextLoadTracker {
  private readonly results = new Map<string, ContextList<unknown>>();

  /** Carrega, registra o desfecho e devolve os itens (vazio quando falhou). */
  async load<T>(label: string, run: () => Promise<readonly T[] | null | undefined>): Promise<T[]> {
    const result = await loadList<T>(label, run);
    this.results.set(label, result as ContextList<unknown>);
    return [...result.items];
  }

  /** Esquece um rótulo — usado quando a cascata é reiniciada acima dele. */
  forget(...labels: readonly string[]): void {
    for (const label of labels) this.results.delete(label);
  }

  statusOf(label: string): ContextList<unknown>["status"] {
    return this.results.get(label)?.status ?? "ok";
  }

  /**
   * O texto do `<option>` vazio. Só diz "nenhum cadastrado" quando a consulta
   * REALMENTE respondeu vazia; negada ou falha dizem isso.
   */
  emptyLabel(label: string, whenEmpty: string, whenReady: string): string {
    const result = this.results.get(label);
    if (!result) return whenReady;
    return emptyOptionLabel(result, whenEmpty, whenReady);
  }

  /** As mensagens do que não pôde ser lido, para um aviso único na tela. */
  issues(): string[] {
    return contextLoadIssues(...this.results.values());
  }

  /** `true` quando alguma leitura do contexto falhou ou foi negada. */
  get hasIssues(): boolean {
    return this.issues().length > 0;
  }

  /**
   * O bloco de aviso pronto, para a página só interpolar. Vazio quando está
   * tudo bem — nunca ocupa espaço sem ter o que dizer.
   */
  banner(): string {
    const issues = this.issues();
    if (issues.length === 0) return "";
    const escape = (v: string) => v.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    return `
      <div class="ctx-load-issues">
        <strong>PARTE DO CONTEXTO NÃO PÔDE SER CARREGADA</strong>
        <ul>${issues.map(m => `<li>${escape(m)}</li>`).join("")}</ul>
      </div>`;
  }
}

/** Estilo do aviso — uma definição só, importada por quem usa o rastreador. */
export const CONTEXT_LOAD_STYLES = `
  .ctx-load-issues { border:1px solid rgba(239,68,68,0.45); background: rgba(239,68,68,0.10); color: var(--ia-danger-fg, #fca5a5); border-radius:6px; padding:10px 12px; margin-bottom:14px; font-size:0.78rem; }
  .ctx-load-issues strong { display:block; font-size:0.72rem; letter-spacing:0.04em; margin-bottom:6px; }
  .ctx-load-issues ul { margin:0; padding-left:18px; line-height:1.6; }
`;
